import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH7_QUESTIONS } from "../data/neetPhysicsCh7Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 7: Rotational Motion (NCERT Class 11 — the rotational-
// dynamics half of "System of Particles and Rotational Motion"; centre-of-mass
// and linear-momentum topics are covered in Chapter 6, not repeated here).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Torque & Angular Momentum", "Moment of Inertia", "Rotational Kinematics & Energy", "Rolling Motion"];

const SECTION_COLORS = {
  "Torque & Angular Momentum":        "#2563eb",
  "Moment of Inertia":                "#7c3aed",
  "Rotational Kinematics & Energy":   "#dc2626",
  "Rolling Motion":                   "#16a34a",
};

const CONCEPTS = [
  // ── Torque & Angular Momentum ───────────────────────────────────────────────
  { section: "Torque & Angular Momentum", title: "Torque (moment of force): τ = r × F",
    explanation: "Torque about a point O is the vector (cross) product of the position vector r (from O to the point where the force acts) and the force F: τ = r × F. Its magnitude is τ = rF sinθ, where θ is the angle between r and F, and its direction (given by the right-hand rule) is perpendicular to the plane containing r and F. Torque measures how effective a force is at producing angular acceleration about O.",
    example: `tau = r . F . sin(theta)

r = 0.5 m, F = 20 N, theta = 30 deg
tau = 0.5 x 20 x sin(30) = 0.5 x 20 x 0.5 = 5 N.m

Torque is MAXIMUM when F is perpendicular to r (theta = 90 deg),
giving tau = rF; it is ZERO when F is along r (theta = 0).` },
  { section: "Torque & Angular Momentum", title: "Angular momentum: L = r × p",
    explanation: "The angular momentum of a particle about a point O is L = r × p = m(r × v), with magnitude L = rp sinθ = mvr sinθ. For a particle moving in a circle of radius r with speed v, r is perpendicular to v (theta = 90 deg), so this simplifies to L = mvr. Angular momentum is the rotational analogue of linear momentum.",
    example: `L = m . v . r   (circular motion, r perpendicular to v)

m = 2 kg, v = 3 m/s, r = 0.5 m
L = 2 x 3 x 0.5 = 3 kg.m^2/s
(direction along the rotation axis, given by the right-hand rule)` },
  { section: "Torque & Angular Momentum", title: "Relation between torque and angular momentum: τ = dL/dt",
    explanation: "Just as net force equals the rate of change of linear momentum (F = dp/dt), the net external torque acting on a system equals the rate of change of its total angular momentum: tau_ext = dL/dt. This is the rotational analogue of Newton's second law, and holds for a single particle, a system of particles, or a rigid body about a fixed point or axis.",
    example: `L(t) = 4 t^2   (kg.m^2/s, some hypothetical system)

tau = dL/dt = 8t

At t = 2 s:  tau = 8 x 2 = 16 N.m` },
  { section: "Torque & Angular Momentum", title: "Conservation of angular momentum",
    explanation: "If the net external torque on a system is zero, its total angular momentum stays constant: dL/dt = 0  =>  L = I.omega = constant. This is why a spinning ice skater (or a person on a rotating stool) speeds up when pulling their arms in -- decreasing I forces omega to increase so that I.omega is unchanged -- and slows down again when the arms are extended outward.",
    example: `I1.omega1 = I2.omega2   (no external torque about the spin axis)

Skater, arms out:  I1 = 5 kg.m^2, omega1 = 2 rad/s
Arms pulled in:    I2 = 2 kg.m^2

omega2 = I1.omega1 / I2 = (5 x 2) / 2 = 5 rad/s
(spins 2.5 times faster after pulling the arms in)` },

  // ── Moment of Inertia ────────────────────────────────────────────────────────
  { section: "Moment of Inertia", title: "Moment of inertia: definition and physical significance",
    explanation: "The moment of inertia (I) of a rigid body about an axis is I = sum of m_i . r_i^2, taken over every particle of the body, where r_i is its PERPENDICULAR distance from the axis. I measures a body's rotational inertia -- its resistance to a change in angular velocity -- playing the same role in rotational dynamics that mass plays in linear dynamics. Unlike mass, I depends on the axis chosen and on how the mass is distributed relative to it.",
    example: `I = sum( m_i . r_i^2 )

Two 1 kg point masses at r = 2 m from the axis, on opposite
ends of a light (massless) rod:
I = (1)(2^2) + (1)(2^2) = 4 + 4 = 8 kg.m^2` },
  { section: "Moment of Inertia", title: "Radius of gyration",
    explanation: "The radius of gyration K of a body about an axis is defined through I = M.K^2, i.e. K = sqrt(I/M). It is the distance from the axis at which, if the body's entire mass were concentrated as a single point, the moment of inertia would be unchanged. K depends on the body's shape and the chosen axis, not on its total mass.",
    example: `Thin rod, length L, about a perpendicular axis through its
centre:  I = M.L^2 / 12

M.K^2 = M.L^2/12   =>   K = L / sqrt(12) = L / (2.sqrt(3))
                       ~ 0.289 L` },
  { section: "Moment of Inertia", title: "Parallel axis theorem",
    explanation: "The parallel axis theorem relates the moment of inertia about ANY axis to the moment of inertia about a parallel axis through the centre of mass: I = I_cm + M.d^2, where M is the total mass and d is the perpendicular distance between the two parallel axes. It applies to a rigid body of any shape.",
    example: `Disc, mass M, radius R.
I_cm (central axis, perpendicular to plane) = (1/2) M R^2

I about a parallel axis through the rim (tangent to the disc,
still perpendicular to its plane), d = R:
I = (1/2) M R^2 + M R^2 = (3/2) M R^2` },
  { section: "Moment of Inertia", title: "Perpendicular axis theorem",
    explanation: "The perpendicular axis theorem applies ONLY to a flat, planar (two-dimensional) lamina lying in the x-y plane: the moment of inertia about an axis (z) perpendicular to the plane equals the sum of the moments of inertia about two mutually perpendicular axes (x and y) lying IN the plane, with all three axes intersecting at one point: I_z = I_x + I_y.",
    example: `Uniform disc (a planar lamina), mass M, radius R.
About the central axis perpendicular to its plane:
I_z = (1/2) M R^2

By symmetry I_x = I_y (both are diameters of the disc):
I_z = I_x + I_y   =>   I_x = I_z / 2 = (1/4) M R^2
(moment of inertia of a disc about any diameter)` },
  { section: "Moment of Inertia", title: "Standard moments of inertia — ring, disc, rod, solid sphere, hollow sphere",
    explanation: "These standard results (each about the natural symmetry axis through the centre of mass) come up constantly in NEET problems and are frequently mixed up -- learn them as a set: Ring (about the central axis, perpendicular to its plane): I = M R^2. Disc (same axis): I = (1/2) M R^2. Thin uniform rod (about the central axis, perpendicular to the rod): I = M L^2 / 12. Solid sphere (about a diameter): I = (2/5) M R^2. Hollow sphere / thin spherical shell (about a diameter): I = (2/3) M R^2.",
    example: `Body                      Axis                          I
------------------------------------------------------------
Ring   (M, R)   through centre, perp. to plane    M R^2
Disc   (M, R)   through centre, perp. to plane    (1/2) M R^2
Rod    (M, L)   through centre, perp. to rod      (1/12) M L^2
Solid sphere (M,R)  about a diameter              (2/5) M R^2
Hollow sphere(M,R)  about a diameter              (2/3) M R^2

Example: M = 3 kg, R = 0.2 m  ->  R^2 = 0.04 m^2
Ring:  I = 3 x 0.04 = 0.12 kg.m^2
Disc:  I = 0.5 x 3 x 0.04 = 0.06 kg.m^2   (exactly half the ring's)` },

  // ── Rotational Kinematics & Energy ──────────────────────────────────────────
  { section: "Rotational Kinematics & Energy", title: "Relating linear and angular kinematic quantities",
    explanation: "For a rigid body rotating about a fixed axis, every particle shares the same angular velocity (omega) and angular acceleration (alpha), but a particle at perpendicular distance r from the axis has a DIFFERENT linear (tangential) speed and acceleration: v = omega.r, and tangential acceleration a_t = alpha.r.",
    example: `v = omega . r          a_t = alpha . r

omega = 10 rad/s, r = 0.3 m  ->  v = 10 x 0.3 = 3 m/s
alpha = 4 rad/s^2            ->  a_t = 4 x 0.3 = 1.2 m/s^2` },
  { section: "Rotational Kinematics & Energy", title: "Equations of rotational motion (constant angular acceleration)",
    explanation: "For constant angular acceleration alpha, the equations of rotational motion mirror the linear kinematics equations exactly, with omega (angular velocity), theta (angular displacement) and alpha (angular acceleration) replacing v, s and a: omega = omega0 + alpha.t ; theta = omega0.t + (1/2)alpha.t^2 ; omega^2 = omega0^2 + 2.alpha.theta.",
    example: `omega0 = 0, alpha = 2 rad/s^2, t = 5 s

omega = omega0 + alpha.t = 0 + 2 x 5 = 10 rad/s
theta = omega0.t + (1/2)alpha.t^2 = 0 + 0.5 x 2 x 25 = 25 rad` },
  { section: "Rotational Kinematics & Energy", title: "Newton's second law for rotation: τ = Iα",
    explanation: "For rotation about a fixed axis, the net external torque equals the moment of inertia times the angular acceleration: tau = I.alpha -- the rotational analogue of F = m.a. This follows directly from tau = dL/dt: since L = I.omega and I is constant for a rigid body about a fixed axis, tau = I.(d omega/dt) = I.alpha.",
    example: `A wheel with I = 4 kg.m^2 is brought from rest to
omega = 20 rad/s in t = 4 s (constant alpha).

alpha = (omega - omega0)/t = 20/4 = 5 rad/s^2
tau = I . alpha = 4 x 5 = 20 N.m` },
  { section: "Rotational Kinematics & Energy", title: "Rotational kinetic energy",
    explanation: "The kinetic energy of a rigid body rotating with angular velocity omega about a fixed axis is KE_rot = (1/2) I omega^2 -- obtained by summing (1/2) m_i v_i^2 over every particle, using v_i = omega.r_i. It is the direct rotational analogue of translational KE = (1/2) m v^2, with I replacing m and omega replacing v.",
    example: `KE_rot = (1/2) I omega^2

I = 0.5 kg.m^2, omega = 6 rad/s
KE_rot = 0.5 x 0.5 x 6^2 = 0.5 x 0.5 x 36 = 9 J` },

  // ── Rolling Motion ───────────────────────────────────────────────────────────
  { section: "Rolling Motion", title: "Rolling without slipping: condition v = ωR",
    explanation: "For a round body of radius R rolling on a surface WITHOUT slipping, the speed of its centre of mass equals v_cm = omega.R, where omega is its angular velocity about the centre. Physically, this is exactly the condition that makes the point of contact with the ground instantaneously at rest (zero velocity) -- there is no relative sliding between the body and the surface at that point.",
    example: `v_cm = omega . R

Wheel: R = 0.25 m, omega = 12 rad/s
v_cm = 0.25 x 12 = 3 m/s

Contact point velocity = v_cm - omega.R = 3 - 3 = 0   (rolling!)
Topmost point velocity = v_cm + omega.R = 3 + 3 = 6 m/s (= 2.v_cm)` },
  { section: "Rolling Motion", title: "Total kinetic energy of a rolling body",
    explanation: "The total kinetic energy of a body rolling without slipping is the sum of its translational KE (of the centre of mass) and its rotational KE (about the centre of mass): KE = (1/2)M v^2 + (1/2)I_cm omega^2. Writing I_cm = M.k^2 (k = radius of gyration) and using the rolling condition v = omega.R, this becomes KE = (1/2) M v^2 (1 + k^2/R^2).",
    example: `KE = (1/2) M v^2 (1 + k^2/R^2)

Solid sphere: M = 2 kg, R = 0.1 m, v = 4 m/s, k^2/R^2 = 2/5

KE = 0.5 x 2 x 4^2 x (1 + 0.4) = 4 x 4 x 1.4 = 22.4 J
(Translational part = 16 J, rotational part = 6.4 J)` },
  { section: "Rolling Motion", title: "Acceleration and speed of a body rolling down an incline",
    explanation: "For a round body rolling without slipping down an incline of angle theta (friction supplies the torque needed for rolling but, since there is no slipping, it does no work), energy conservation gives its speed at the bottom, and Newton's second law along the incline gives its acceleration: v = sqrt( 2gh / (1 + k^2/R^2) )  and  a = g.sin(theta) / (1 + k^2/R^2). A body with a SMALLER k^2/R^2 (mass concentrated closer to the axis) accelerates faster.",
    example: `a = g.sin(theta) / (1 + k^2/R^2)

Solid sphere (k^2/R^2 = 2/5) on a 30 deg incline, g = 10 m/s^2:
a = 10 x sin(30) / (1 + 0.4) = 10 x 0.5 / 1.4 ~ 3.57 m/s^2

Compare a ring (k^2/R^2 = 1):
a = 10 x 0.5 / (1 + 1) = 5/2 = 2.5 m/s^2   (the sphere wins)` },
  { section: "Rolling Motion", title: "Comparing rolling bodies: order of arrival down an incline",
    explanation: "Since a = g.sin(theta)/(1 + k^2/R^2), the SMALLER the ratio k^2/R^2, the LARGER the acceleration -- and this ratio depends only on the body's SHAPE, not on its mass or radius. So identical-shaped objects of any mass/size, released together, always take the same time; and across shapes, the order from fastest to slowest down the same incline is always: solid sphere, then solid cylinder/disc, then hollow sphere, then hollow cylinder/ring.",
    example: `k^2/R^2 values (shape only, independent of M and R):
Solid sphere        : 2/5 = 0.40   <- smallest -> fastest
Solid cylinder/disc : 1/2 = 0.50
Hollow sphere        : 2/3 = 0.67
Ring / hollow cyl.   : 1   = 1.00  <- largest  -> slowest

Arrival order (fastest to slowest):
solid sphere > disc > hollow sphere > ring` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh7Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh7Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh7Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH7_QUESTIONS : NEET_PHYSICS_CH7_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 7</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Rotational Motion</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 11 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH7_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH7_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
